import { useEffect } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CookieBanner from '../../components/feature/CookieBanner';
import StructuredData from '../../components/feature/StructuredData';
import ScrollToTop from '../../components/feature/ScrollToTop';

const EmpresaPage = () => {
  useEffect(() => {
    document.title = 'Empresa de Vaciado de Pisos Barcelona - BIOTRAUMA';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Conoce BIOTRAUMA BARCELONA, empresa especializada en vaciado de pisos y locales. Equipo profesional, servicio discreto y gestión responsable de residuos en Barcelona.'
      );
    }

    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'Empresa de Vaciado de Pisos Barcelona - BIOTRAUMA');
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:title');
      meta.content = 'Empresa de Vaciado de Pisos Barcelona - BIOTRAUMA';
      document.head.appendChild(meta);
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute(
        'content',
        'Conoce BIOTRAUMA BARCELONA, empresa especializada en vaciado de pisos y locales. Equipo profesional, servicio discreto y gestión responsable de residuos en Barcelona.'
      );
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:description');
      meta.content = 'Conoce BIOTRAUMA BARCELONA, empresa especializada en vaciado de pisos y locales. Equipo profesional, servicio discreto y gestión responsable de residuos en Barcelona.';
      document.head.appendChild(meta);
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', 'https://barcelona.biotrauma.es/empresa');
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:url');
      meta.content = 'https://barcelona.biotrauma.es/empresa';
      document.head.appendChild(meta);
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://barcelona.biotrauma.es/empresa');
    }
  }, []);

  const values = [
    {
      icon: 'ri-shield-check-line',
      title: 'Profesionalidad',
      description: 'Equipo experimentado con años de trayectoria en el sector del vaciado de inmuebles.',
    },
    {
      icon: 'ri-time-line',
      title: 'Rapidez',
      description: 'Cumplimos los plazos acordados sin comprometer la calidad del servicio.',
    },
    {
      icon: 'ri-eye-off-line',
      title: 'Discreción',
      description: 'Trabajamos con total respeto a la privacidad y las circunstancias de cada cliente.',
    },
    {
      icon: 'ri-recycle-line',
      title: 'Responsabilidad',
      description: 'Gestión correcta de residuos en puntos autorizados y donación de objetos en buen estado.',
    },
  ];

  const features = [
    {
      icon: 'ri-team-line',
      title: 'Equipo cualificado',
      description: 'Personal formado y con experiencia en vaciados de todo tipo de inmuebles.',
    },
    {
      icon: 'ri-shield-line',
      title: 'Protección garantizada',
      description: 'Protegemos ascensores, escaleras y zonas comunes durante todo el proceso.',
    },
    {
      icon: 'ri-building-line',
      title: 'Coordinación con fincas',
      description: 'Nos coordinamos con administradores de fincas y porterías para facilitar el trabajo.',
    },
    {
      icon: 'ri-file-list-line',
      title: 'Documentación completa',
      description: 'Entregamos certificados de gestión de residuos y toda la documentación necesaria.',
    },
  ];

  return (
    <>
      <StructuredData type="empresa" />
      <Navbar />
      <main>
        <section className="relative h-[40vh] min-h-[400px] flex items-center overflow-hidden pt-20 md:pt-0">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage:
                'url(https://public.readdy.ai/ai/img_res/1e7c8fb684f6ba3cc56a7624d7b17d2e.jpg)',
            }}
          />
          <div className="absolute inset-0 bg-navy/75" />

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-center py-8">
            <nav className="text-white/80 text-sm mb-4">
              <a href="/" className="hover:text-white cursor-pointer">Inicio</a>
              <span className="mx-2">/</span>
              <span className="text-white">Empresa</span>
            </nav>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-5xl md:text-6xl font-bold text-white mb-5"
            >
              Nuestra empresa
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-white/90 max-w-2xl mx-auto"
            >
              Especialistas en vaciado de pisos y locales en Barcelona con años de experiencia
            </motion.p>
          </div>
        </section>

        <section className="bg-white py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <span className="inline-block bg-orange-bg text-orange text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-4">
                  Quiénes somos
                </span>
                <h2 className="text-4xl font-bold text-navy mb-6">
                  BIOTRAUMA BARCELONA, tu empresa de confianza
                </h2>
                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    Somos una empresa especializada en el <strong>vaciado de pisos, casas, locales comerciales y trasteros en Barcelona</strong> y toda el área metropolitana. Con años de experiencia en el sector, nos hemos consolidado como referente en <a href="/servicios" className="text-orange hover:underline font-medium">servicios de desalojo y retirada de enseres</a>.
                  </p>
                  <p>
                    Nuestro equipo está formado por profesionales cualificados que entienden que cada situación es única. Ya sea por una herencia, una mudanza, un desahucio o una reforma, ofrecemos un servicio adaptado a las necesidades específicas de cada cliente.
                  </p>
                  <p>
                    Trabajamos con <strong>administradores de fincas, agencias inmobiliarias y particulares</strong>, garantizando siempre la máxima discreción, rapidez y profesionalidad. Nos encargamos de todo el proceso: desde la valoración inicial hasta la entrega del inmueble completamente vacío y limpio.
                  </p>
                  <p>
                    La gestión responsable de residuos es uno de nuestros pilares fundamentales. Todos los enseres retirados se llevan a puntos autorizados, y aquellos objetos en buen estado se donan a entidades sociales siempre que es posible. Si tienes dudas sobre nuestro proceso, consulta nuestras <a href="/faq" className="text-orange hover:underline font-medium">preguntas frecuentes</a>.
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="rounded-2xl overflow-hidden shadow-xl">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20house%20clearance%20team%20working%20in%20Barcelona%20apartment%20carrying%20furniture%20with%20protective%20equipment&width=800&height=1000&seq=empresa-team-img-002&orientation=portrait"
                    alt="Equipo BIOTRAUMA Barcelona"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 bg-orange text-white p-8 rounded-2xl shadow-lg max-w-xs">
                  <div className="text-4xl font-bold mb-2">+10 años</div>
                  <p className="text-sm">de experiencia en vaciado de inmuebles en Barcelona</p>
                </div>
              </motion.div>
            </div>

            <div className="mb-20">
              <h3 className="text-3xl font-bold text-navy text-center mb-12">
                Nuestros valores
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {values.map((value, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="text-center"
                  >
                    <div className="w-16 h-16 bg-orange-bg rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className={`${value.icon} text-3xl text-orange`}></i>
                    </div>
                    <h4 className="text-xl font-bold text-navy mb-3">{value.title}</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">{value.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="bg-gray-50 rounded-3xl p-12">
              <h3 className="text-3xl font-bold text-navy text-center mb-4">
                Cómo trabajamos
              </h3>
              <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
                Nuestro método de trabajo garantiza un servicio eficiente, seguro y respetuoso con el inmueble y las zonas comunes del edificio. Descubre <a href="/servicios" className="text-orange hover:underline font-medium">todos los servicios que ofrecemos</a>.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {features.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex gap-4"
                  >
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-orange rounded-xl flex items-center justify-center">
                        <i className={`${feature.icon} text-xl text-white`}></i>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-navy mb-2">{feature.title}</h4>
                      <p className="text-sm text-gray-600 leading-relaxed">{feature.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-navy py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold text-white mb-5">
              ¿Necesitas un vaciado profesional?
            </h2>
            <p className="text-lg text-white/85 mb-10">
              Contacta con nosotros y te daremos un presupuesto personalizado sin compromiso. Consulta nuestras <a href="/faq" className="text-white hover:text-orange underline font-medium">preguntas frecuentes</a> o llámanos directamente.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://wa.me/34629501941"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center bg-orange text-white text-base font-medium px-8 py-4 rounded-full hover:bg-orange-light hover:scale-105 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line text-xl mr-2"></i>
                Contactar por WhatsApp
              </a>
              <a
                href="/contacto"
                className="inline-flex items-center justify-center border-2 border-white bg-transparent text-white text-base font-medium px-8 py-4 rounded-full hover:bg-white hover:text-navy transition-all whitespace-nowrap cursor-pointer"
              >
                Ver datos de contacto
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <CookieBanner />
      <ScrollToTop />
    </>
  );
};

export default EmpresaPage;
